import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import CardImage from "./CardImage";

export default function CardLightbox({ open, onClose, images = [], title }) {
  const [index, setIndex] = useState(0);
  const [zoomed, setZoomed] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(0);
      setZoomed(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, images.length]);

  const has = images.length > 0;
  const multi = images.length > 1;
  const next = () => {
    setZoomed(false);
    setIndex((i) => (i + 1) % images.length);
  };
  const prev = () => {
    setZoomed(false);
    setIndex((i) => (i - 1 + images.length) % images.length);
  };

  return createPortal(
    <AnimatePresence>
      {open && has && (
        <motion.div
          className="fixed inset-0 z-[60] flex flex-col bg-black/95 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="card-lightbox"
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-5 py-4 shrink-0">
            <div className="min-w-0">
              <p className="text-[11px] font-mono uppercase tracking-widest text-blue-400">
                {images[index]?.label || "Card"}
              </p>
              {title && <p className="text-sm text-slate-300 truncate">{title}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="lightbox-zoom-toggle"
                onClick={() => setZoomed((z) => !z)}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label={zoomed ? "Zoom out" : "Zoom in"}
              >
                {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
              </button>
              <button
                data-testid="lightbox-close-button"
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Image stage */}
          <div ref={areaRef} className="relative flex-1 overflow-hidden flex items-center justify-center px-4">
            {multi && (
              <>
                <button
                  data-testid="lightbox-prev-button"
                  onClick={prev}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  aria-label="Previous"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  data-testid="lightbox-next-button"
                  onClick={next}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                  aria-label="Next"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                className={`select-none ${zoomed ? "cursor-zoom-out" : "cursor-zoom-in"}`}
                drag={zoomed ? true : multi ? "x" : false}
                dragElastic={0.2}
                dragConstraints={zoomed ? areaRef : { left: 0, right: 0 }}
                onClick={() => setZoomed((z) => !z)}
                onDragEnd={(e, info) => {
                  if (zoomed) return;
                  if (info.offset.x < -70) next();
                  else if (info.offset.x > 70) prev();
                }}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: zoomed ? 2.2 : 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
              >
                <CardImage
                  path={images[index].path}
                  className="max-h-[70vh] max-w-[92vw] w-auto h-auto object-contain rounded-xl pointer-events-none"
                />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dots */}
          {multi && (
            <div className="flex items-center justify-center gap-2 py-5 shrink-0">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setZoomed(false);
                    setIndex(i);
                  }}
                  className={`h-2 rounded-full transition-all ${
                    i === index ? "w-6 bg-blue-500" : "w-2 bg-white/30 hover:bg-white/50"
                  }`}
                  aria-label={img.label}
                />
              ))}
            </div>
          )}
          {!multi && <div className="py-5 shrink-0 text-center text-xs text-slate-500">Tap image to zoom</div>}
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
